/**
 * The randomness the cards come from.
 *
 * This file is separate from the Monte Carlo generator in the solver pipeline, and the
 * separation is deliberate. Those are two different jobs:
 *
 *   The solver integrates. It needs a fast, well-distributed, reproducible stream, and
 *   `mulberry32` is the right tool — nobody can gain anything by predicting which
 *   board a simulation will draw next.
 *
 *   A deck is a game of chance. If the stream is predictable, the game is broken, and
 *   "predictable" includes "the seed space is small enough to search". That is not
 *   hypothetical: in 1999 an online poker room's shuffle was broken exactly this way,
 *   because a 32-bit seed can only produce four billion decks and a 52-card deck has
 *   8 x 10^67 orderings. Every other deck was impossible, and the ones that were
 *   possible could be enumerated.
 *
 * So the deck gets a real cipher. ChaCha20 (RFC 8439) and SHA-256 (FIPS 180-4), both
 * implemented here in plain TypeScript so they run synchronously inside the engine,
 * and both checked against their published test vectors in the test suite — which is
 * the first thing anyone reviewing this would ask for, and the only way to know an
 * implementation of a standard is actually that standard.
 */

/* ------------------------------------------------------------------ ChaCha20 --- */

const ROTL = (x: number, n: number): number => ((x << n) | (x >>> (32 - n))) >>> 0;

/**
 * One ChaCha20 block: 64 bytes of keystream from a 32-byte key, 12-byte nonce and a
 * block counter. RFC 8439 section 2.3.
 *
 * Exported so the test suite can drive it with the RFC's own worked example, which is
 * the only evidence that this is ChaCha20 rather than something that merely produces
 * noise. Output is the sixteen state words; serialising them little-endian gives the
 * keystream bytes the RFC prints.
 */
export function chachaBlock(
  key: Uint32Array, counter: number, nonce: Uint32Array, out: Uint32Array
): void {
  // "expand 32-byte k", the constant every ChaCha implementation starts from.
  const s = out;
  s[0] = 0x61707865; s[1] = 0x3320646e; s[2] = 0x79622d32; s[3] = 0x6b206574;
  for (let i = 0; i < 8; i++) s[4 + i] = key[i]!;
  s[12] = counter >>> 0;
  s[13] = nonce[0]!; s[14] = nonce[1]!; s[15] = nonce[2]!;

  const w = s.slice();

  const qr = (a: number, b: number, c: number, d: number) => {
    w[a] = (w[a]! + w[b]!) >>> 0; w[d] = ROTL(w[d]! ^ w[a]!, 16);
    w[c] = (w[c]! + w[d]!) >>> 0; w[b] = ROTL(w[b]! ^ w[c]!, 12);
    w[a] = (w[a]! + w[b]!) >>> 0; w[d] = ROTL(w[d]! ^ w[a]!, 8);
    w[c] = (w[c]! + w[d]!) >>> 0; w[b] = ROTL(w[b]! ^ w[c]!, 7);
  };

  // Twenty rounds: ten iterations of four column rounds then four diagonal rounds.
  for (let i = 0; i < 10; i++) {
    qr(0, 4, 8, 12); qr(1, 5, 9, 13); qr(2, 6, 10, 14); qr(3, 7, 11, 15);
    qr(0, 5, 10, 15); qr(1, 6, 11, 12); qr(2, 7, 8, 13); qr(3, 4, 9, 14);
  }
  for (let i = 0; i < 16; i++) s[i] = (w[i]! + s[i]!) >>> 0;
}

/**
 * A stream of unbiased integers, from a 32-byte seed.
 *
 * The counter is 32 bits and each block is 16 words, so one instance yields 2^36
 * words before it would repeat — around 68 billion. A hand of poker consumes about
 * sixty. It throws rather than wrapping, because silently repeating a keystream is
 * the one failure mode of a stream cipher that destroys everything built on it.
 */
export class CryptoRng {
  private key = new Uint32Array(8);
  private nonce = new Uint32Array(3);
  private block = new Uint32Array(16);
  private counter = 0;
  private used = 16;

  constructor(seed: Uint8Array, label = "") {
    const material = label ? sha256(concat(seed, utf8(label))) : seed;
    if (material.length !== 32) throw new Error(`a seed is 32 bytes, not ${material.length}`);
    for (let i = 0; i < 8; i++) {
      this.key[i] = (material[i * 4]! | (material[i * 4 + 1]! << 8)
                   | (material[i * 4 + 2]! << 16) | (material[i * 4 + 3]! << 24)) >>> 0;
    }
  }

  /** The next 32 bits of keystream. */
  nextUint32(): number {
    if (this.used >= 16) {
      if (this.counter === 0xffffffff) {
        throw new Error("keystream exhausted: refusing to repeat a ChaCha20 counter");
      }
      chachaBlock(this.key, this.counter++, this.nonce, this.block);
      this.used = 0;
    }
    return this.block[this.used++]!;
  }

  /**
   * A uniform integer in [0, n), with no modulo bias.
   *
   * Taking `nextUint32() % n` is biased whenever n does not divide 2^32: the low
   * residues come up more often. For n = 52 the bias is about one part in eighty
   * million, which sounds like nothing until you remember that a bias in a shuffle is
   * exactly what a review looks for and exactly what cannot be argued away. Rejecting
   * the values above the largest whole multiple of n removes it entirely, at the cost
   * of a redraw roughly once every eighty million calls.
   */
  below(n: number): number {
    if (!Number.isInteger(n) || n <= 0) throw new Error(`below(${n}) is not a range`);
    if (n === 1) return 0;
    const limit = Math.floor(0x100000000 / n) * n;
    let x = this.nextUint32();
    while (x >= limit) x = this.nextUint32();
    return x % n;
  }

  /** A float in [0, 1). For callers that want one; the deck does not. */
  next(): number { return this.nextUint32() / 0x100000000; }
}

/* -------------------------------------------------------------------- SHA-256 --- */

const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const ROTR = (x: number, n: number): number => ((x >>> n) | (x << (32 - n))) >>> 0;

/** SHA-256 of a byte string. FIPS 180-4. */
export function sha256(msg: Uint8Array): Uint8Array {
  const h = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a,
    0x510e527f, 0x9b05688c, 0x1f83d9ab, 0x5be0cd19,
  ]);

  // Pad to a multiple of 64 bytes: a 1 bit, zeroes, then the length in bits as 64-bit
  // big-endian. The length goes in as a float-safe pair, since a byte length beyond
  // 2^53 bits is not reachable in this runtime anyway.
  const bitLen = msg.length * 8;
  const padded = new Uint8Array(((msg.length + 9 + 63) >> 6) << 6);
  padded.set(msg);
  padded[msg.length] = 0x80;
  const dv = new DataView(padded.buffer);
  dv.setUint32(padded.length - 8, Math.floor(bitLen / 0x100000000) >>> 0);
  dv.setUint32(padded.length - 4, bitLen >>> 0);

  const w = new Uint32Array(64);
  for (let off = 0; off < padded.length; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4);
    for (let i = 16; i < 64; i++) {
      const s0 = ROTR(w[i - 15]!, 7) ^ ROTR(w[i - 15]!, 18) ^ (w[i - 15]! >>> 3);
      const s1 = ROTR(w[i - 2]!, 17) ^ ROTR(w[i - 2]!, 19) ^ (w[i - 2]! >>> 10);
      w[i] = (w[i - 16]! + s0 + w[i - 7]! + s1) >>> 0;
    }
    let [a, b, c, d, e, f, g, hh] = [h[0]!, h[1]!, h[2]!, h[3]!, h[4]!, h[5]!, h[6]!, h[7]!];
    for (let i = 0; i < 64; i++) {
      const S1 = ROTR(e, 6) ^ ROTR(e, 11) ^ ROTR(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (hh + S1 + ch + K[i]! + w[i]!) >>> 0;
      const S0 = ROTR(a, 2) ^ ROTR(a, 13) ^ ROTR(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      hh = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    h[0] = (h[0]! + a) >>> 0; h[1] = (h[1]! + b) >>> 0; h[2] = (h[2]! + c) >>> 0;
    h[3] = (h[3]! + d) >>> 0; h[4] = (h[4]! + e) >>> 0; h[5] = (h[5]! + f) >>> 0;
    h[6] = (h[6]! + g) >>> 0; h[7] = (h[7]! + hh) >>> 0;
  }

  const out = new Uint8Array(32);
  const odv = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) odv.setUint32(i * 4, h[i]!);
  return out;
}

/* ---------------------------------------------------------------------- seeds --- */

export const SEED_BYTES = 32;

/** A seed, as raw bytes or as the hex string a hand history records. */
export type CryptoSeed = Uint8Array | string;

/**
 * A fresh seed from the platform's cryptographic random source.
 *
 * 256 bits, because a 52-card deck has 52! orderings and reaching all of them takes
 * 226. Anything smaller makes most decks impossible before a single card is dealt,
 * and no amount of statistical testing of the ones that ARE possible fixes that.
 *
 * There is no fallback to Math.random(). A generator that quietly degrades to
 * something predictable is worse than one that refuses, because the failure is
 * invisible in exactly the deployment where it matters.
 */
export function randomSeed(): Uint8Array {
  const g = globalThis as { crypto?: { getRandomValues?: <T extends Uint8Array>(a: T) => T } };
  if (!g.crypto?.getRandomValues) {
    throw new Error(
      "no cryptographic random source available: refusing to deal from a predictable one");
  }
  return g.crypto.getRandomValues(new Uint8Array(SEED_BYTES));
}

/**
 * A 32-byte seed derived from a short, memorable label.
 *
 * For TESTS, fuzzing and reproducing a reported hand — never for dealing to a player.
 * It is shaped like a seed but it carries only as much entropy as the label does, so
 * `seedFromLabel(7)` is exactly as guessable as the number seven. That is precisely
 * what a test wants and precisely what a game must not have, which is why it is named
 * for what it is rather than looking like an ordinary seed constructor.
 */
export function seedFromLabel(label: string | number): Uint8Array {
  return sha256(utf8(`bluffbusters/labelled-seed/${label}`));
}

/**
 * A seed derived from another one plus a label.
 *
 * Domain separation. A session seed produces a seed per hand, and each hand's seed
 * produces one stream for the deck and a different one for the bots — so recording a
 * hand's seed replays that hand exactly, and no stream can be inferred from another.
 */
export function deriveSeed(parent: Uint8Array, label: string): Uint8Array {
  return sha256(concat(parent, utf8(label)));
}

/* ----------------------------------------------------------------- byte plumbing --- */

export function toHex(b: Uint8Array): string {
  let s = "";
  for (const x of b) s += x.toString(16).padStart(2, "0");
  return s;
}

export function fromHex(hex: string): Uint8Array {
  const clean = hex.trim().toLowerCase();
  if (!/^[0-9a-f]*$/.test(clean) || clean.length % 2 !== 0) {
    throw new Error("a seed is written as an even number of hex digits");
  }
  const out = new Uint8Array(clean.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function utf8(s: string): Uint8Array {
  const out = new Uint8Array(s.length * 3);
  let n = 0;
  for (const ch of s) {
    const c = ch.codePointAt(0)!;
    if (c < 0x80) out[n++] = c;
    else if (c < 0x800) { out[n++] = 0xc0 | (c >> 6); out[n++] = 0x80 | (c & 63); }
    else if (c < 0x10000) {
      out[n++] = 0xe0 | (c >> 12); out[n++] = 0x80 | ((c >> 6) & 63); out[n++] = 0x80 | (c & 63);
    } else {
      out[n++] = 0xf0 | (c >> 18); out[n++] = 0x80 | ((c >> 12) & 63);
      out[n++] = 0x80 | ((c >> 6) & 63); out[n++] = 0x80 | (c & 63);
    }
  }
  return out.slice(0, n);
}

export function concat(...parts: Uint8Array[]): Uint8Array {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}
